/**
 * Advanced type safety utilities with branded types, discriminated unions, and strict validation
 */

import { z } from 'zod';
import { logger } from '../logger/index.js';
import type { LogContext } from '../logger/index.js';
import type {
  UserId,
  TweetId,
  Email,
  Username,
  Token,
  Hash,
  Timestamp,
  Count,
  Percentage,
  RequestId
} from '../types/branded.js';
import {
  isUserId,
  isTweetId,
  isEmail,
  isUsername,
  isToken,
  isHash,
  isTimestamp,
  isCount,
  isPercentage,
  createUserId,
  createTweetId,
  createEmail,
  createUsername,
  createToken,
  createHash,
  createTimestamp,
  createCount,
  createPercentage,
  generateRequestId
} from '../types/branded.js';

// Re-export branded types and utilities
export type {
  UserId,
  TweetId,
  Email,
  Username,
  Token,
  Hash,
  Timestamp,
  Count,
  Percentage,
  RequestId
} from '../types/branded.js';

export {
  isUserId,
  isTweetId,
  isEmail,
  isUsername,
  isToken,
  isHash,
  isTimestamp,
  isCount,
  isPercentage,
  createUserId,
  createTweetId,
  createEmail,
  createUsername,
  createToken,
  createHash,
  createTimestamp,
  createCount,
  createPercentage,
  generateRequestId
} from '../types/branded.js';

// Discriminated union patterns for PODx
export type ScrapingStatus = 
  | { type: 'pending'; queuedAt: Timestamp }
  | { type: 'running'; startedAt: Timestamp; progress: Percentage }
  | { type: 'completed'; completedAt: Timestamp; tweetCount: Count }
  | { type: 'failed'; failedAt: Timestamp; error: string; retryable: boolean }
  | { type: 'cancelled'; cancelledAt: Timestamp; reason: string };

export type AuthenticationStatus =
  | { type: 'unauthenticated' }
  | { type: 'authenticating'; startedAt: Timestamp }
  | { type: 'authenticated'; authenticatedAt: Timestamp; expiresAt: Timestamp }
  | { type: 'expired'; expiredAt: Timestamp }
  | { type: 'failed'; failedAt: Timestamp; reason: string; retryable: boolean };

export type AnalysisResult =
  | { type: 'success'; data: any; confidence: Percentage; processedAt: Timestamp }
  | { type: 'partial'; data: any; confidence: Percentage; warnings: string[]; processedAt: Timestamp }
  | { type: 'failed'; error: string; attemptedAt: Timestamp; retryable: boolean };

export type DatabaseOperation =
  | { type: 'create'; entity: string; data: any }
  | { type: 'read'; entity: string; id: string }
  | { type: 'update'; entity: string; id: string; data: any }
  | { type: 'delete'; entity: string; id: string }
  | { type: 'query'; entity: string; filters: Record<string, any> };

// Exhaustive pattern matching utilities
export const matchScrapingStatus = <T>(
  status: ScrapingStatus,
  handlers: {
    pending: (data: { queuedAt: Timestamp }) => T;
    running: (data: { startedAt: Timestamp; progress: Percentage }) => T;
    completed: (data: { completedAt: Timestamp; tweetCount: Count }) => T;
    failed: (data: { failedAt: Timestamp; error: string; retryable: boolean }) => T;
    cancelled: (data: { cancelledAt: Timestamp; reason: string }) => T;
  }
): T => {
  switch (status.type) {
    case 'pending':
      return handlers.pending({ queuedAt: status.queuedAt });
    case 'running':
      return handlers.running({ startedAt: status.startedAt, progress: status.progress });
    case 'completed':
      return handlers.completed({ completedAt: status.completedAt, tweetCount: status.tweetCount });
    case 'failed':
      return handlers.failed({ failedAt: status.failedAt, error: status.error, retryable: status.retryable });
    case 'cancelled':
      return handlers.cancelled({ cancelledAt: status.cancelledAt, reason: status.reason });
    default:
      // Exhaustiveness check
      const _exhaustive: never = status;
      throw new Error(`Unhandled scraping status: ${JSON.stringify(_exhaustive)}`);
  }
};

export const matchAuthenticationStatus = <T>(
  status: AuthenticationStatus,
  handlers: {
    unauthenticated: () => T;
    authenticating: (data: { startedAt: Timestamp }) => T;
    authenticated: (data: { authenticatedAt: Timestamp; expiresAt: Timestamp }) => T;
    expired: (data: { expiredAt: Timestamp }) => T;
    failed: (data: { failedAt: Timestamp; reason: string; retryable: boolean }) => T;
  }
): T => {
  switch (status.type) {
    case 'unauthenticated':
      return handlers.unauthenticated();
    case 'authenticating':
      return handlers.authenticating({ startedAt: status.startedAt });
    case 'authenticated':
      return handlers.authenticated({ authenticatedAt: status.authenticatedAt, expiresAt: status.expiresAt });
    case 'expired':
      return handlers.expired({ expiredAt: status.expiredAt });
    case 'failed':
      return handlers.failed({ failedAt: status.failedAt, reason: status.reason, retryable: status.retryable });
    default:
      const _exhaustive: never = status;
      throw new Error(`Unhandled authentication status: ${JSON.stringify(_exhaustive)}`);
  }
};

// Zod schemas for runtime validation
export const UserIdSchema = z.string().min(1).transform((val) => val as UserId);
export const TweetIdSchema = z.string().regex(/^\d+$/).transform((val) => val as TweetId);
export const EmailSchema = z.string().email().transform((val) => val as Email);
export const UsernameSchema = z.string().regex(/^[a-zA-Z0-9_]{1,15}$/).transform((val) => val as Username);
export const TokenSchema = z.string().min(32).transform((val) => val as Token);
export const HashSchema = z.string().regex(/^[a-f0-9]{32,}$/i).transform((val) => val as Hash);
export const TimestampSchema = z.number().positive().int().transform((val) => val as Timestamp);
export const CountSchema = z.number().int().min(0).transform((val) => val as Count);
export const PercentageSchema = z.number().min(0).max(100).transform((val) => val as Percentage);

// Discriminated union schemas
export const ScrapingStatusSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pending'),
    queuedAt: TimestampSchema
  }),
  z.object({
    type: z.literal('running'),
    startedAt: TimestampSchema,
    progress: PercentageSchema
  }),
  z.object({
    type: z.literal('completed'),
    completedAt: TimestampSchema,
    tweetCount: CountSchema
  }),
  z.object({
    type: z.literal('failed'),
    failedAt: TimestampSchema,
    error: z.string(),
    retryable: z.boolean()
  }),
  z.object({
    type: z.literal('cancelled'),
    cancelledAt: TimestampSchema,
    reason: z.string()
  })
]);

export const AuthenticationStatusSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('unauthenticated') }),
  z.object({
    type: z.literal('authenticating'),
    startedAt: TimestampSchema
  }),
  z.object({
    type: z.literal('authenticated'),
    authenticatedAt: TimestampSchema,
    expiresAt: TimestampSchema
  }),
  z.object({
    type: z.literal('expired'),
    expiredAt: TimestampSchema
  }),
  z.object({
    type: z.literal('failed'),
    failedAt: TimestampSchema,
    reason: z.string(),
    retryable: z.boolean()
  })
]);

// Runtime validation utilities
export class TypeValidator {
  /**
   * Validate and transform data using Zod schema
   */
  static validate<T>(schema: z.ZodSchema<T>, data: unknown, context?: LogContext): T {
    try {
      return schema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const validationError = {
          message: 'Validation failed',
          errors: error.errors.map(err => ({
            path: err.path.join('.'),
            message: err.message,
            code: err.code
          }))
        };
        
        logger.error('Type validation failed', {
          ...context,
          validationError
        });
        
        throw new Error(`Validation failed: ${validationError.errors.map(e => `${e.path}: ${e.message}`).join(', ')}`);
      }
      throw error;
    }
  }

  /**
   * Safe validation that returns Result instead of throwing
   */
  static safeValidate<T>(schema: z.ZodSchema<T>, data: unknown, context?: LogContext): 
    { success: true; data: T } | { success: false; error: string } {
    try {
      const result = schema.parse(data);
      return { success: true, data: result };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map(err => `${err.path.join('.')}: ${err.message}`).join(', ');
        logger.warn('Type validation failed (safe)', {
          ...context,
          error: errorMessage
        });
        return { success: false, error: errorMessage };
      }
      return { success: false, error: 'Unknown validation error' };
    }
  }

  /**
   * Validate branded type
   */
  static validateBranded<T extends string>(
    value: unknown,
    validator: (val: string) => val is T,
    typeName: string,
    context?: LogContext
  ): T {
    if (typeof value !== 'string') {
      throw new Error(`Expected ${typeName}, got ${typeof value}`);
    }
    
    if (!validator(value)) {
      throw new Error(`Invalid ${typeName}: ${value}`);
    }
    
    return value as T;
  }
}

// Type-safe configuration management
export interface TypeSafeConfig {
  readonly database: {
    readonly url: string;
    readonly maxConnections: Count;
    readonly timeout: Count;
  };
  readonly api: {
    readonly port: Count;
    readonly rateLimit: Count;
    readonly timeout: Count;
  };
  readonly scraping: {
    readonly maxConcurrent: Count;
    readonly delay: Count;
    readonly maxRetries: Count;
  };
  readonly authentication: {
    readonly tokenExpiry: Count;
    readonly refreshThreshold: Percentage;
  };
}

export const ConfigSchema = z.object({
  database: z.object({
    url: z.string().url(),
    maxConnections: CountSchema,
    timeout: CountSchema
  }),
  api: z.object({
    port: CountSchema,
    rateLimit: CountSchema,
    timeout: CountSchema
  }),
  scraping: z.object({
    maxConcurrent: CountSchema,
    delay: CountSchema,
    maxRetries: CountSchema
  }),
  authentication: z.object({
    tokenExpiry: CountSchema,
    refreshThreshold: PercentageSchema
  })
});

// Type-safe environment variable parsing
export class EnvironmentValidator {
  /**
   * Parse and validate environment variables
   */
  static parseEnv<T>(schema: z.ZodSchema<T>, env: Record<string, string | undefined>): T {
    try {
      return schema.parse(env);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const missingVars = error.errors
          .filter(err => err.code === 'invalid_type' && err.received === 'undefined')
          .map(err => err.path.join('_').toUpperCase());
        
        if (missingVars.length > 0) {
          throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
        }
        
        const invalidVars = error.errors
          .filter(err => err.code !== 'invalid_type' || err.received !== 'undefined')
          .map(err => `${err.path.join('_').toUpperCase()}: ${err.message}`);
        
        if (invalidVars.length > 0) {
          throw new Error(`Invalid environment variables: ${invalidVars.join(', ')}`);
        }
      }
      throw error;
    }
  }

  /**
   * Create environment schema with common validations
   */
  static createEnvSchema<T extends Record<string, z.ZodTypeAny>>(schema: T): z.ZodSchema<z.infer<z.ZodObject<T>>> {
    return z.object(schema);
  }
}

// Type-safe API response patterns
export type ApiResponse<T> = 
  | { success: true; data: T; timestamp: Timestamp; requestId: RequestId }
  | { success: false; error: string; code?: string; timestamp: Timestamp; requestId: RequestId };

export const createApiResponse = <T>(
  data: T,
  requestId: RequestId
): ApiResponse<T> => ({
  success: true,
  data,
  timestamp: createTimestamp(Date.now()),
  requestId
});

export const createApiError = (
  error: string,
  code?: string,
  requestId?: RequestId
): ApiResponse<never> => ({
  success: false,
  error,
  code,
  timestamp: createTimestamp(Date.now()),
  requestId: requestId || createRequestId()
});

// Utility to create RequestId
export const createRequestId = (): RequestId => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` as RequestId;
};

// Type-safe event system
export interface TypedEvent<T = any> {
  readonly type: string;
  readonly data: T;
  readonly timestamp: Timestamp;
  readonly id: string;
}

export class TypeSafeEventEmitter<T extends Record<string, any>> {
  private listeners = new Map<keyof T, Array<(data: T[keyof T]) => void>>();

  on<K extends keyof T>(event: K, listener: (data: T[K]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(listener);
  }

  emit<K extends keyof T>(event: K, data: T[K]): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const typedEvent: TypedEvent<T[K]> = {
        type: event as string,
        data,
        timestamp: createTimestamp(Date.now()),
        id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      
      eventListeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          logger.error('Event listener error', {
            event: event as string,
            error: (error as Error).message
          });
        }
      });
    }
  }

  off<K extends keyof T>(event: K, listener: (data: T[K]) => void): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(listener);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }
}

// Export main type safety utilities
export const typeSafety = {
  branded: {
    create: {
      userId: createUserId,
      tweetId: createTweetId,
      email: createEmail,
      username: createUsername,
      token: createToken,
      hash: createHash,
      timestamp: createTimestamp,
      count: createCount,
      percentage: createPercentage
    },
    validate: {
      isUserId,
      isTweetId,
      isEmail,
      isUsername,
      isToken,
      isHash,
      isTimestamp,
      isCount,
      isPercentage
    }
  },
  discriminated: {
    match: {
      scrapingStatus: matchScrapingStatus,
      authenticationStatus: matchAuthenticationStatus
    }
  },
  validation: {
    validator: TypeValidator,
    environment: EnvironmentValidator,
    schemas: {
      UserIdSchema,
      TweetIdSchema,
      EmailSchema,
      UsernameSchema,
      TokenSchema,
      HashSchema,
      TimestampSchema,
      CountSchema,
      PercentageSchema,
      ScrapingStatusSchema,
      AuthenticationStatusSchema,
      ConfigSchema
    }
  },
  api: {
    createResponse: createApiResponse,
    createError: createApiError,
    createRequestId
  },
  events: TypeSafeEventEmitter
};
